import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import { getSustainableActionList } from '../../../api/sustainableActions/sustainableActions';
import { RoutesPathE } from '../../../declarations/enums/routes';
import { SustainableActionI } from '../../../declarations/interfaces/sustainableAction';
import useReactRouter from '../../../hooks/useReactRouter';
import useCarbonOffsets, {
  CarbonOffsetQueryT,
  SelectedCarbonOffsetProjectT,
} from '../../CarbonOffsetsPage/hooks/useCarbonOffsets';

export type SustainableItemsQueryT = {
  id: number;
  qty: number;
  totalCharge: number;
};

type QueryT = {
  sustainableItems: SustainableItemsQueryT[];
  carbonOffsets?: CarbonOffsetQueryT[];
};

type ParsedRootSustainableActionT = { qty: number; totalCharge: number } & SustainableActionI;
export type TranslatedRootSustainableActionsT = Omit<
  ParsedRootSustainableActionT,
  'country' | 'description' | 'impact' | 'name' | 'projectOwner' | 'type'
> & {
  country: string;
  description: string;
  impact: string;
  name: string;
  projectOwner: string;
  type: string;
};

export type SelectedSustainableActionsT = Pick<
  ParsedRootSustainableActionT,
  'id' | 'name' | 'pricePerUnit' | 'country' | 'type' | 'primaryCurrency' | 'totalCharge' | 'qty'
>;

const useSustainableActions = () => {
  const { navigateTo, getUrlQueryParams } = useReactRouter();
  const { isLoading: isLoadingCO, selectedCarbonOffsetProjects } = useCarbonOffsets();
  const { sustainableItems = [], carbonOffsets } = getUrlQueryParams<QueryT>();
  const [parsedRootSustainableActions, setParsedRootSustainableActions] = useState<
    ParsedRootSustainableActionT[]
  >([]);
  const [preparedSustainableActionsQueryData, setPreparedSustainableActionsQueryData] =
    useState<SustainableItemsQueryT[]>(sustainableItems);
  const [selectedSustainableActions, setSelectedSustainableActions] = useState<
    SelectedSustainableActionsT[]
  >([]);
  const [selectedCarbonOffsets, setSelectedCarbonOffsets] =
    useState<SelectedCarbonOffsetProjectT[]>();
  const [preparedCarbonOffsetQueryData, setPreparedCarbonOffsetQueryData] = useState(carbonOffsets);

  const { isLoading: isLoadingSA, isFetching: isFetchingSA } = useQuery(
    ['sustainableActionsList'],
    () => getSustainableActionList(),
    {
      select: data => data.data.items,
      onSuccess(data) {
        const selectedActions: SelectedSustainableActionsT[] = [];

        const ParsedRootSustainableActions = data.map(item => {
          const matchedQueryItem = sustainableItems.find(({ id }) => item.id === id);
          const qty = matchedQueryItem?.qty || 0;
          const totalCharge = matchedQueryItem?.totalCharge || 0;

          if (!!qty) {
            selectedActions.push({
              id: item.id,
              name: item.name,
              pricePerUnit: item.pricePerUnit,
              primaryCurrency: item.primaryCurrency,
              country: item.country,
              type: item.type,
              totalCharge,
              qty,
            });
          }

          return {
            ...item,
            qty,
            totalCharge,
          };
        });

        setParsedRootSustainableActions(ParsedRootSustainableActions);

        if (selectedActions.length) {
          setSelectedSustainableActions(selectedActions);
        }
      },
    }
  );

  const onChangeSustainableAction = ({
    selectedId,
    updatedAction,
  }: {
    selectedId: number;
    updatedAction: Partial<ParsedRootSustainableActionT>;
  }) => {
    const updatedRootSustainableActions =
      parsedRootSustainableActions?.map(item =>
        item.id === selectedId
          ? {
              ...item,
              ...updatedAction,
            }
          : item
      ) || [];

    const selectedActions = updatedRootSustainableActions
      .filter(({ qty }) => qty)
      .map(({ id, name, pricePerUnit, primaryCurrency, country, type, qty, totalCharge }) => ({
        id,
        name,
        pricePerUnit,
        primaryCurrency,
        country,
        type,
        totalCharge,
        qty,
      }));

    setParsedRootSustainableActions(updatedRootSustainableActions);
    setSelectedSustainableActions(selectedActions);
    setPreparedSustainableActionsQueryData(
      selectedActions.map(({ id, qty, totalCharge }) => ({ id, qty, totalCharge }))
    );
  };

  const onPurchase = () => {
    navigateTo(
      RoutesPathE.PAYMENT_DETAILS_PAGE,
      {
        sustainableItems: preparedSustainableActionsQueryData,
        ...(preparedCarbonOffsetQueryData && { carbonOffsets: preparedCarbonOffsetQueryData }),
      },
      true
    );
  };

  const onGoToCarbonOffsets = () =>
    navigateTo(
      RoutesPathE.CARBON_OFFSETS_PAGE,
      {
        sustainableItems: preparedSustainableActionsQueryData,
        ...(preparedCarbonOffsetQueryData && { carbonOffsets: preparedCarbonOffsetQueryData }),
      },
      true
    );

  const onDeleteCarbonOffsets = (id: number) => {
    const updatedSelectedCarbonOffsetList = selectedCarbonOffsets?.filter(item => item.id !== id);

    setSelectedCarbonOffsets(updatedSelectedCarbonOffsetList);
    setPreparedCarbonOffsetQueryData(
      updatedSelectedCarbonOffsetList?.map(({ id: selectedId, selectedQty }) => ({
        id: selectedId,
        qty: selectedQty,
      }))
    );
  };

  const handleClickBackButton = () => {
    if (!!carbonOffsets) {
      navigateTo(RoutesPathE.CARBON_OFFSETS_PAGE, {}, true);

      return;
    }

    navigateTo(RoutesPathE.HOME_PAGE);
  };

  useEffect(() => {
    if (!!carbonOffsets) {
      setSelectedCarbonOffsets(selectedCarbonOffsetProjects);
    }
  }, [selectedCarbonOffsetProjects]);

  return {
    isLoading: isLoadingSA || isFetchingSA || isLoadingCO,
    sustainableActions: parsedRootSustainableActions,
    carbonOffsets: selectedCarbonOffsets,
    selectedSustainableActions,
    onChangeSustainableAction,
    onPurchase,
    onDeleteCarbonOffsets,
    onGoToCarbonOffsets,
    handleClickBackButton,
  };
};

export default useSustainableActions;
